import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Circle,
  Square,
  Loader2,
  ChevronRight,
  Mic,
  Camera as CameraIcon,
  Video,
  RotateCcw,
  Send,
  Gauge,
  Timer,
  Sparkles,
} from "lucide-react";

const MAX_RECORD_MS = 120000; // 2 minutes

export default function InterviewRoom() {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autostart = searchParams.get("autostart") === "1";
  const autostartedRef = useRef(false);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const recordedBlobRef = useRef(null);
  const recordedUrlRef = useRef(null);

  const [interview, setInterview] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  // phase: loading | error | ready | recording | review | uploading
  const [phase, setPhase] = useState("loading");
  const [elapsed, setElapsed] = useState(0);
  const [recordedDuration, setRecordedDuration] = useState(0);
  const [permissionError, setPermissionError] = useState(null);
  const [quality, setQuality] = useState(null);
  const [qualityLoading, setQualityLoading] = useState(false);
  const previewReqIdRef = useRef(0);

  // Fetch interview
  useEffect(() => {
    api
      .get(`/interviews/${interviewId}`)
      .then(({ data }) => {
        setInterview(data);
        setQuestions(data.questions || []);
        const answered = (data.answers || []).length;
        setCurrentIdx(
          Math.min(answered, Math.max(0, (data.questions || []).length - 1))
        );
        setPhase("ready");
      })
      .catch(() => {
        toast.error("Interview not found");
        setPhase("error");
      });
  }, [interviewId]);

  // Camera init
  const initCamera = useCallback(async () => {
    if (streamRef.current) {
      attachLiveStream();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      streamRef.current = stream;
      attachLiveStream();
    } catch {
      setPermissionError(
        "Camera and microphone permission is required. Please allow access and refresh the page."
      );
    }
  }, []);

  const attachLiveStream = () => {
    if (!videoRef.current || !streamRef.current) return;
    videoRef.current.srcObject = streamRef.current;
    videoRef.current.removeAttribute("src");
    videoRef.current.muted = true;
    videoRef.current.controls = false;
    videoRef.current.play().catch(() => {});
  };

  const attachPlayback = (url) => {
    if (!videoRef.current) return;
    videoRef.current.srcObject = null;
    videoRef.current.src = url;
    videoRef.current.muted = false;
    videoRef.current.controls = true;
    videoRef.current.currentTime = 0;
  };

  useEffect(() => {
    if (phase === "ready" || phase === "recording") {
      initCamera();
    }
  }, [phase, initCamera, currentIdx]);

  // Magic-link autostart: tap-to-record from invite email (?autostart=1)
  useEffect(() => {
    if (!autostart || autostartedRef.current) return;
    if (phase !== "ready") return;
    if (currentIdx !== 0) return;
    let cancelled = false;
    (async () => {
      // Wait briefly for camera to be ready, polling up to ~3s
      for (let i = 0; i < 15; i++) {
        if (cancelled) return;
        if (streamRef.current) break;
        await new Promise((r) => setTimeout(r, 200));
      }
      if (cancelled || !streamRef.current) return;
      autostartedRef.current = true;
      toast.message("Auto-starting recording…");
      startRecording();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autostart, phase, currentIdx]);

  // Unmount cleanup
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (timerRef.current) clearInterval(timerRef.current);
      if (recordedUrlRef.current) {
        URL.revokeObjectURL(recordedUrlRef.current);
        recordedUrlRef.current = null;
      }
    };
  }, []);

  const clearRecorded = () => {
    if (recordedUrlRef.current) {
      URL.revokeObjectURL(recordedUrlRef.current);
      recordedUrlRef.current = null;
    }
    recordedBlobRef.current = null;
    setRecordedDuration(0);
  };

  const startRecording = () => {
    if (!streamRef.current) {
      toast.error("Camera not ready");
      return;
    }
    clearRecorded();
    chunksRef.current = [];
    let mime = "video/webm;codecs=vp8,opus";
    if (!window.MediaRecorder || !MediaRecorder.isTypeSupported(mime)) {
      mime = "video/webm";
    }
    const recorder = new MediaRecorder(streamRef.current, { mimeType: mime });
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = handleRecordingStopped;
    mediaRecorderRef.current = recorder;
    recorder.start();
    setElapsed(0);
    setPhase("recording");
    const startedAt = Date.now();
    timerRef.current = setInterval(() => {
      const e = Date.now() - startedAt;
      setElapsed(e);
      if (e >= MAX_RECORD_MS) stopRecording();
    }, 200);
  };

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
  };

  const handleRecordingStopped = () => {
    const blob = new Blob(chunksRef.current, { type: "video/webm" });
    recordedBlobRef.current = blob;
    setRecordedDuration(elapsed);
    const url = URL.createObjectURL(blob);
    recordedUrlRef.current = url;
    setPhase("review");
    requestAnimationFrame(() => attachPlayback(url));
    fetchQualityPreview(blob, elapsed);
  };

  const fetchQualityPreview = async (blob, durationMs) => {
    const reqId = ++previewReqIdRef.current;
    setQuality(null);
    setQualityLoading(true);
    const fd = new FormData();
    fd.append("duration_ms", String(durationMs));
    fd.append("file", blob, "preview.webm");
    try {
      const { data } = await api.post(
        `/interviews/${interviewId}/preview`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (reqId === previewReqIdRef.current) {
        setQuality(data);
      }
    } catch {
      // silent — preview is best-effort
    } finally {
      if (reqId === previewReqIdRef.current) setQualityLoading(false);
    }
  };

  const reRecord = () => {
    previewReqIdRef.current += 1; // invalidate any in-flight preview
    setQuality(null);
    setQualityLoading(false);
    clearRecorded();
    setElapsed(0);
    setPhase("ready");
    requestAnimationFrame(() => attachLiveStream());
  };

  const submitAnswer = async () => {
    const blob = recordedBlobRef.current;
    if (!blob) {
      toast.error("No recording to submit");
      return;
    }
    setPhase("uploading");
    const question = questions[currentIdx];
    const fd = new FormData();
    fd.append("question_id", question.id);
    fd.append("file", blob, `answer-${question.id}.webm`);
    try {
      await api.post(`/interviews/${interviewId}/answers`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Answer submitted");
      clearRecorded();
      setQuality(null);
      setQualityLoading(false);
      previewReqIdRef.current += 1;
      const isLast = currentIdx >= questions.length - 1;
      if (isLast) {
        await api.post(`/interviews/${interviewId}/complete`);
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        navigate(`/interview/session/${interviewId}/complete`);
      } else {
        setCurrentIdx((i) => i + 1);
        setElapsed(0);
        setPhase("ready");
        requestAnimationFrame(() => attachLiveStream());
      }
    } catch {
      toast.error("Upload failed. Please try again.");
      setPhase("review");
      requestAnimationFrame(() =>
        recordedUrlRef.current && attachPlayback(recordedUrlRef.current)
      );
    }
  };

  if (phase === "loading" || !interview) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-900 text-white">
        Loading interview…
      </main>
    );
  }
  if (phase === "error") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-900 text-white">
        Interview not found.
      </main>
    );
  }

  const question = questions[currentIdx];
  const progress =
    ((currentIdx + (phase === "uploading" ? 1 : 0)) / questions.length) * 100;
  const seconds = Math.floor(elapsed / 1000);
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  const recSec = Math.floor(recordedDuration / 1000);
  const recMm = String(Math.floor(recSec / 60)).padStart(2, "0");
  const recSs = String(recSec % 60).padStart(2, "0");

  return (
    <main
      data-testid="interview-room-page"
      className="grain-overlay min-h-screen bg-slate-900 text-white"
    >
      <div className="mx-auto flex min-h-screen max-w-5xl flex-col px-4 py-4 sm:px-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-blue-300 sm:text-xs">
            <Video className="h-3 w-3" /> {interview.job_title}
          </div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 sm:text-xs">
            Question {currentIdx + 1} of {questions.length}
          </div>
        </div>
        <Progress
          value={progress}
          className="mt-3 h-1 bg-white/10"
          data-testid="interview-progress"
        />

        {/* Question */}
        <div className="mt-6 border border-white/10 bg-white/5 p-4 sm:mt-8 sm:p-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-300 sm:text-xs">
            Question
          </div>
          <p
            data-testid="interview-question-text"
            className="mt-2 font-heading text-lg font-medium leading-snug sm:mt-3 sm:text-2xl md:text-3xl"
          >
            {question?.text}
          </p>
        </div>

        {/* Video */}
        <div className="relative mt-4 overflow-hidden border border-white/10 bg-black sm:mt-6">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="aspect-video w-full bg-black object-cover"
            data-testid="interview-video-preview"
          />
          {phase === "recording" && (
            <div className="absolute left-2 top-2 flex items-center gap-1.5 border border-red-500 bg-black/60 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-red-400 sm:left-4 sm:top-4 sm:gap-2 sm:px-3 sm:tracking-[0.2em] sm:text-xs">
              <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
              Rec · {mm}:{ss}
            </div>
          )}
          {phase === "review" && (
            <div
              data-testid="review-banner"
              className="absolute left-2 top-2 border border-blue-400/60 bg-black/60 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-blue-300 sm:left-4 sm:top-4 sm:px-3 sm:tracking-[0.2em] sm:text-xs"
            >
              Preview · {recMm}:{recSs}
            </div>
          )}
          {phase === "uploading" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-center">
              <div>
                <Loader2 className="mx-auto mb-3 h-7 w-7 animate-spin text-blue-300 sm:h-8 sm:w-8" />
                <div className="font-heading text-base sm:text-lg">
                  Uploading & transcribing…
                </div>
                <div className="mt-1 text-xs text-slate-400">
                  We're scoring your answer with AI.
                </div>
              </div>
            </div>
          )}
        </div>

        {permissionError && (
          <div className="mt-4 border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300">
            {permissionError}
          </div>
        )}

        {phase === "review" && (
          <div
            data-testid="quality-panel"
            className="mt-4 border border-white/10 bg-white/5 p-4 sm:mt-6 sm:p-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-blue-300 sm:text-xs sm:tracking-[0.2em]">
                <Sparkles className="h-3 w-3" />
                Answer quality preview
              </div>
              {qualityLoading && (
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Analyzing…
                </div>
              )}
            </div>

            {!quality && !qualityLoading && (
              <div className="mt-4 text-xs text-slate-400">
                Preview unavailable — you can still submit your answer.
              </div>
            )}

            {quality && (
              <>
                <div className="mt-4 grid grid-cols-2 gap-px bg-white/10 sm:grid-cols-4">
                  <div className="bg-slate-900/40 p-3 sm:p-4">
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                      <Gauge className="h-3 w-3" /> Quality
                    </div>
                    <div
                      data-testid="quality-score"
                      className="kpi-stat mt-2 font-heading text-2xl font-semibold text-blue-300 sm:text-3xl"
                    >
                      {quality.quality_score}
                      <span className="text-base text-slate-500">/10</span>
                    </div>
                  </div>
                  <div className="bg-slate-900/40 p-3 sm:p-4">
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                      <Timer className="h-3 w-3" /> Length
                    </div>
                    <div className="kpi-stat mt-2 font-heading text-xl font-semibold sm:text-2xl">
                      {quality.duration_seconds}s
                    </div>
                  </div>
                  <div className="bg-slate-900/40 p-3 sm:p-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                      Pace
                    </div>
                    <div className="kpi-stat mt-2 font-heading text-xl font-semibold sm:text-2xl">
                      {quality.wpm}
                      <span className="text-xs text-slate-500"> wpm</span>
                    </div>
                  </div>
                  <div className="bg-slate-900/40 p-3 sm:p-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                      Fillers
                    </div>
                    <div className="kpi-stat mt-2 font-heading text-xl font-semibold sm:text-2xl">
                      {quality.filler_count}
                      <span className="text-xs text-slate-500"> · {quality.filler_pct}%</span>
                    </div>
                  </div>
                </div>

                {quality.hints?.length > 0 && (
                  <ul className="mt-4 space-y-2 text-sm text-slate-300">
                    {quality.hints.map((h, i) => (
                      <li
                        key={i}
                        data-testid={`quality-hint-${i}`}
                        className="flex items-start gap-2"
                      >
                        <span className="mt-1 h-1.5 w-1.5 flex-none rounded-full bg-blue-400" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {quality.transcript && (
                  <details className="mt-4 text-sm">
                    <summary className="cursor-pointer text-xs font-bold uppercase tracking-[0.2em] text-slate-400 hover:text-slate-200">
                      View transcript
                    </summary>
                    <p className="mt-3 whitespace-pre-wrap text-slate-300">
                      {quality.transcript}
                    </p>
                  </details>
                )}
              </>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="mt-5 flex flex-col gap-3 sm:mt-6 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
          <div className="flex flex-wrap items-center gap-3 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 sm:gap-4 sm:text-xs sm:tracking-[0.2em]">
            <span className="inline-flex items-center gap-2">
              <CameraIcon className="h-3 w-3" /> Camera
            </span>
            <span className="inline-flex items-center gap-2">
              <Mic className="h-3 w-3" /> Mic
            </span>
            <span>Limit · 2 min</span>
          </div>

          {phase === "ready" && (
            <Button
              data-testid="start-recording-btn"
              onClick={startRecording}
              className="w-full rounded-sm bg-red-600 py-5 hover:bg-red-500 sm:w-auto sm:px-6 sm:py-6"
            >
              <Circle className="mr-2 h-4 w-4 fill-current" />
              Start recording
            </Button>
          )}

          {phase === "recording" && (
            <Button
              data-testid="stop-recording-btn"
              onClick={stopRecording}
              className="w-full rounded-sm bg-white py-5 text-slate-900 hover:bg-slate-100 sm:w-auto sm:px-6 sm:py-6"
            >
              <Square className="mr-2 h-4 w-4 fill-current" />
              Stop recording
            </Button>
          )}

          {phase === "review" && (
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
              <Button
                data-testid="re-record-btn"
                variant="outline"
                onClick={reRecord}
                className="w-full rounded-sm border-white/30 bg-transparent py-5 text-white hover:bg-white/10 sm:w-auto sm:px-5 sm:py-6"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Re-record
              </Button>
              <Button
                data-testid="submit-answer-btn"
                onClick={submitAnswer}
                className="w-full rounded-sm bg-blue-600 py-5 hover:bg-blue-500 sm:w-auto sm:px-6 sm:py-6"
              >
                <Send className="mr-2 h-4 w-4" />
                Submit answer
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}

          {phase === "uploading" && (
            <Button
              disabled
              className="w-full rounded-sm bg-blue-700 py-5 sm:w-auto sm:px-6 sm:py-6"
            >
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing…
            </Button>
          )}
        </div>
      </div>
    </main>
  );
}
